import React from "react";
import { SWRConfig } from "swr";
import { APIError, UnauthorizedAPIError } from "../data/api";
import { useAuth } from "./auth_portal_provider";
import * as Sentry from "@sentry/browser";

interface AuthPortalSWRClientProps {
  children?: React.ReactNode;
}

export function AuthPortalSWRClient(props: AuthPortalSWRClientProps) {
  const { children } = props;
  const { token, getTokenSilently, setToken } = useAuth();

  return (
    <SWRConfig
      value={{
        fetcher: async (input: RequestInfo) => {
          try {
            const responseBody = await get(input, token);
            return responseBody;
          } catch (error) {
            if (error instanceof UnauthorizedAPIError) {
              const newToken = await getTokenSilently();
              setToken(newToken);

              const responseBody = await get(input, newToken);
              return responseBody;
            }

            // let other errors populate up
            throw error;
          }
        },
      }}
    >
      {children}
    </SWRConfig>
  );
}

async function get(input: RequestInfo, token: string) {
  const res = await fetch(input, {
    headers: {
      "Flexspace-Client": "partners-portal",
      ...(token && {
        Authorization: `Bearer ${token}`,
      }),
    },
  });

  const body = await res.json();

  if (res.status >= 200 && res.status < 300) {
    return body;
  }

  if (res.status === 401) {
    throw new UnauthorizedAPIError(body.message);
  }

  if (res.status >= 400) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        input,
        httpStatusCode: res.status,
      });
      Sentry.captureMessage(`Portal: Fail to request ${input}`);
    });
  }

  throw new APIError(body.message);
}
